'use client';

import { Clock, CheckCircle2, XCircle, Server } from 'lucide-react';
import { CurlGenerator } from './curl-generator';
import { MethodBadge } from './method-badge';
import type { ExecutionResult, RequestConfig, HttpMethod } from '@/lib/integrations/types';

interface TechnicalTabProps {
  result: ExecutionResult;
  requestConfig: RequestConfig;
  connectionAlias: string;
}

export function TechnicalTab({
  result,
  requestConfig,
  connectionAlias,
}: TechnicalTabProps) {
  const isSuccess = result.status >= 200 && result.status < 300;

  return (
    <div className="space-y-6">
      {/* Request Summary */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">Request Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Method:</span>
            <MethodBadge method={requestConfig.method} size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Status:</span>
            <span className={`flex items-center gap-1 ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
              {isSuccess ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {result.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Duration:</span>
            <span className="font-mono">{result.durationMs}ms</span>
          </div>
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Provider:</span>
            <span className="font-mono">{result.provider}</span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t">
          <span className="text-muted-foreground text-sm">Endpoint:</span>
          <code className="ml-2 text-sm font-mono bg-muted px-2 py-0.5 rounded">
            {result.endpoint}
          </code>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Executed at {new Date(result.timestamp).toLocaleString()}
        </div>
      </div>

      {/* Curl Command */}
      <div>
        <h3 className="text-sm font-medium mb-3">cURL Command</h3>
        <CurlGenerator
          method={requestConfig.method}
          endpoint={requestConfig.endpoint}
          alias={connectionAlias}
          body={requestConfig.body}
        />
      </div>

      {/* Error Details */}
      {result.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h3 className="text-sm font-medium text-destructive mb-2">Error</h3>
          <pre className="text-sm font-mono whitespace-pre-wrap text-destructive">
            {result.error}
          </pre>
        </div>
      )}
    </div>
  );
}

export default TechnicalTab;
